import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { ErrorState } from "@arcadeai/design-system/components/ui/molecules/error-state";
import { captureException, withScope } from "@sentry/react";
import { Link, useRouter } from "@tanstack/react-router";
import { Home, RotateCcw } from "lucide-react";
import { useCallback, useEffect } from "react";

type CustomErrorComponentProps = {
  error: Error;
  info?: {
    componentStack: string;
  };
  reset: () => void;
};

export function CustomErrorComponent({
  error,
  info,
  reset,
}: CustomErrorComponentProps) {
  const router = useRouter();

  useEffect(() => {
    withScope((scope) => {
      scope.setTag("errorBoundary", "tanstack-router");
      scope.setLevel("error");
      scope.setContext("router", {
        pathname: window.location.pathname,
        search: window.location.search,
        hash: window.location.hash,
        timestamp: new Date().toISOString(),
      });
      if (info) {
        scope.setContext("info", info);
      }
      captureException(error);
    });
  }, [error, info]);

  const handleTryAgain = useCallback(() => {
    reset();
    try {
      router.invalidate();
    } catch {
      window.location.reload();
    }
  }, [reset, router]);

  const stackTrace = [
    error.stack,
    info?.componentStack ? `\n\nComponent Stack:${info.componentStack}` : "",
  ]
    .filter(Boolean)
    .join("");

  return (
    <ErrorState
      errorMessage={error.message || "An unknown error occurred"}
      errorStack={stackTrace}
    >
      <Button className="flex-1" onClick={handleTryAgain} variant="outline">
        <RotateCcw className="mr-2 h-4 w-4" />
        Try Again
      </Button>
      <Button className="flex-1" render={<Link to="/" />}>
        <Home className="mr-2 h-4 w-4" />
        Go Home
      </Button>
    </ErrorState>
  );
}
